import { isAuth } from '../auth/isAuth';
import AuthService from '../auth/handleTokenProvider';

type FetchOptions = RequestInit & {
  headers?: Record<string, string>;
};

type RefreshFailure = { refreshFailed: true };

function isRefreshFailure(
  value: Response | RefreshFailure,
): value is RefreshFailure {
  return 'refreshFailed' in value && value.refreshFailed === true;
}

export async function apiFetch(
  endpoint: string,
  options: FetchOptions = {},
  isMedia = false,
  isAws3 = false,
  isSpecial = false,
): Promise<unknown> {
  const user = isAuth();
  const token = user && user.token;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(options.method === 'PATCH' ? { 'Content-Type': 'application/merge-patch+json' } : {}),
    ...(options.method !== 'PATCH' && !isMedia ? { 'Content-Type': 'application/ld+json' } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };

  const classicOptions: FetchOptions = {
    ...options,
    headers,
  };

  let response: Response;
  try {
    response = isAws3
      ? await fetch(endpoint)
      : await fetch(process.env.REACT_APP_BASE_URL + endpoint, classicOptions);
  } catch (e) {
    console.error("Erreur réseau lors de l'appel API :", e);
    return { error: true, networkError: true };
  }

  let responseAfterRefresh: Response | RefreshFailure | null = null;

  if (response.status === 401) {
    responseAfterRefresh = await handleRefreshToken(user, endpoint, options, isAws3);
  }

  if (responseAfterRefresh) {
    if (isRefreshFailure(responseAfterRefresh)) {
      AuthService.logout();
      return { authFailed: true };
    }
    return handleResponse(responseAfterRefresh, isSpecial);
  }

  return handleResponse(response, isSpecial);
}

async function handleRefreshToken(
  user: ReturnType<typeof isAuth>,
  endpoint: string,
  options: FetchOptions = {},
  isAws3?: boolean,
): Promise<Response | RefreshFailure> {
  if (!user || !user.refresh_token) {
    return { refreshFailed: true };
  }

  const data = { refresh_token: `${user.refresh_token}` };
  const refreshOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify(data),
  };

  try {
    const refreshNeeded = await fetch(
      process.env.REACT_APP_BASE_URL + '/token/refresh',
      refreshOptions,
    );

    if (refreshNeeded.ok) {
      const refreshData = await refreshNeeded.json();
      localStorage.setItem('REACT_TOKEN_AUTH', JSON.stringify(refreshData));

      const optionsAfterRefresh: FetchOptions = {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshData.token}`,
        },
      };

      return isAws3
        ? fetch(endpoint, optionsAfterRefresh)
        : fetch(process.env.REACT_APP_BASE_URL + endpoint, optionsAfterRefresh);
    }

    return { refreshFailed: true };
  } catch (e) {
    console.error('Erreur lors du refresh token :', e);
    return { refreshFailed: true };
  }
}

async function handleResponse(response: Response, isSpecial: boolean) {
  if (isSpecial) return response;

  const isDownloadOrCodex = !specialCase(response);
  if (isDownloadOrCodex) return response;

  return response.status !== 204 ? response.json() : response;
}

function specialCase(response: Response) {
  return !response.url.endsWith('/download') && !response.url.startsWith('https://codex');
}
